import {
  Code2,
  GraduationCap,
  Smartphone,
  Video,
  Wrench,
} from 'lucide-react'
import { BentoGrid, BentoTile } from '@/shared/ui/bento'
import { Reveal } from '@/shared/motion/reveal'
import { Section, SectionHeader } from '@/shared/ui/section'
import { defineT, useTranslation } from '@/shared/i18n/use-translation'
import type { IconComponent } from '@/shared/types/icon'
import { cn } from '@/shared/lib/cn'
import { asset } from '@/shared/lib/asset'

/**
 * Services section — was previously "Specialties" mixing personal
 * skills with site features. Now it's a proper 5-card services grid
 * (web, mobile, AI video, teaching, tools) that maps to what the
 * user actually offers as a freelancer/agency-of-one.
 *
 * The "Featured" tile is AI Video Production because that's the most
 * differentiated service and the visual register lands hardest.
 */

const T = defineT({
  eyebrow: { ar: 'خدماتي', en: 'Services' },
  title: {
    ar: 'اللي بقدّمه — 5 خدمات مترابطة',
    en: 'What I offer — 5 connected services',
  },
  description: {
    ar: 'خبرة سنين في 5 مجالات بتشتغل مع بعض في كل مشروع. ما تحتاجش تجمع 5 فريلانسرز — أنا بغطّي السلسلة كاملة.',
    en: 'Years of experience across 5 connected disciplines. You don\'t need 5 freelancers — I cover the full chain.',
  },
  // Tiles
  webTitle: { ar: 'تصميم وتطوير المواقع', en: 'Web Design & Development' },
  webDesc: {
    ar: 'مواقع كاملة من التصميم لـ deploy على Cloudflare Workers. React، TypeScript، edge-first، وكود نضيف جاهز للإنتاج. الموقع ده نفسه شغلي.',
    en: 'End-to-end sites from design to deploy. React, TypeScript, edge-first on Cloudflare Workers. Production-ready code. This site is my own work.',
  },
  mobileTitle: { ar: 'تصميم تطبيقات الهاتف', en: 'Mobile App Design' },
  mobileDesc: {
    ar: 'UI/UX لتطبيقات iOS و Android. تصميم متّسق، تجربة مستخدم محسوبة، و prototypes تفاعلية في Figma جاهزة للتطوير.',
    en: 'iOS and Android UI/UX. Coherent visual systems, considered UX, and interactive Figma prototypes ready for engineering.',
  },
  videoTitle: { ar: 'تصميم فيديوهات AI', en: 'AI Video Production' },
  videoDesc: {
    ar: 'فيديوهات سينمائية بالكامل بـ AI: Sora، Veo، Kling، Runway. سكربت، تصميم لقطات، توليد، montage — حلقة كاملة.',
    en: 'Cinematic AI-generated video. Sora, Veo, Kling, Runway. Script, shot design, generation, and edit — full pipeline.',
  },
  teachTitle: { ar: 'التعليم والإرشاد', en: 'Teaching & Mentorship' },
  teachDesc: {
    ar: 'أكتر من 1,500 طالب اتعلم معايا. أسلوبي عملي: مش محاضرات، مشاريع حقيقية ونتايج تقدر تشوفها.',
    en: '1,500+ students. Hands-on, project-first — not lectures. You ship real work as you learn.',
  },
  buildTitle: { ar: 'بناء الأدوات والحلول', en: 'Building Tools & Solutions' },
  buildDesc: {
    ar: 'أبني أدوات AI و automation بتوفّر وقت لمئات المبدعين. تكامل APIs، Workers، Workflows، MCPs.',
    en: 'AI and automation tools that save hours for hundreds of creators. API integrations, Workers, Workflows, MCPs.',
  },
})

interface Tile {
  key: string
  icon: IconComponent
  titleKey: keyof typeof T
  descKey: keyof typeof T
  image: string
  imageOpacity?: 'soft' | 'medium' | 'strong'
  col: 1 | 2 | 3
  row: 1 | 2
  featured?: boolean
}

const TILES: Tile[] = [
  {
    key: 'video',
    icon: Video,
    titleKey: 'videoTitle',
    descKey: 'videoDesc',
    // TODO: replace with /card-video.webp once generated.
    image: '/card-imagery.webp',
    imageOpacity: 'medium',
    featured: true,
    col: 3,
    row: 2,
  },
  {
    key: 'web',
    icon: Code2,
    titleKey: 'webTitle',
    descKey: 'webDesc',
    image: '/card-engineering.webp',
    imageOpacity: 'soft',
    col: 3,
    row: 1,
  },
  {
    key: 'mobile',
    icon: Smartphone,
    titleKey: 'mobileTitle',
    descKey: 'mobileDesc',
    image: '/card-mobile.webp',
    imageOpacity: 'soft',
    col: 3,
    row: 1,
  },
  {
    key: 'teach',
    icon: GraduationCap,
    titleKey: 'teachTitle',
    descKey: 'teachDesc',
    image: '/card-teaching.webp',
    imageOpacity: 'soft',
    col: 3,
    row: 1,
  },
  {
    key: 'build',
    icon: Wrench,
    titleKey: 'buildTitle',
    descKey: 'buildDesc',
    image: '/card-building.webp',
    imageOpacity: 'soft',
    col: 3,
    row: 1,
  },
]

const OPACITY_MAP = {
  soft: 'opacity-25 group-hover:opacity-40',
  medium: 'opacity-35 group-hover:opacity-50',
  strong: 'opacity-50 group-hover:opacity-65',
} as const

export function Specialties() {
  const { t } = useTranslation(T)

  return (
    <Section id="services" pad="md">
      <Reveal>
        <SectionHeader
          eyebrow={t('eyebrow')}
          title={t('title')}
          description={t('description')}
        />
      </Reveal>

      <div className="stagger">
        <BentoGrid>
          {tilesRender(t)}
        </BentoGrid>
      </div>
    </Section>
  )

  function tilesRender(t: (key: keyof typeof T) => string) {
    return TILES.map((tile) => (
      <Reveal key={tile.key} className="contents">
        <BentoTile
          col={tile.col}
          row={tile.row}
          featured={tile.featured}
          className="relative isolate flex flex-col overflow-hidden"
        >
          <img
            src={asset(tile.image)}
            alt=""
            aria-hidden
            loading="lazy"
            decoding="async"
            className={cn(
              'pointer-events-none absolute inset-0 -z-10 h-full w-full object-cover transition-opacity duration-500',
              OPACITY_MAP[tile.imageOpacity ?? 'soft'],
            )}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 -z-[5] bg-gradient-to-t from-card via-card/80 to-card/40"
          />

          <span
            className="grid h-12 w-12 place-items-center rounded-xl bg-primary/15 text-primary ring-1 ring-primary/30 backdrop-blur-md"
            aria-hidden
          >
            <tile.icon className="h-6 w-6" />
          </span>
          <h3 className="mt-5 font-display text-xl font-bold tracking-tight">
            {t(tile.titleKey)}
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            {t(tile.descKey)}
          </p>
        </BentoTile>
      </Reveal>
    ))
  }
}
